import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';

import { AppState } from '../../store/state/app.state';
import { FormInputChangedModel, PaymentRecommendationModel, ResponseList, ResponseModel } from '../../shared/models';
import { DisplayToaster } from '../../shared/decorators/toaster';
import { CreditNoteModel, CreditNotesCountersModel, CreditNotesListTotalsModel } from '../models';
import { CreditNotePurposeEnum, CreditNotesListTabsEnum } from '../enums';
import { FilterModelNew } from '../../outgoing-invoice/models/filter-model-new';
import { PaymentDirectionEnum, PaymentModel } from '../../payment/models/payment.model';
import { ChangeStatusOperationsEnum } from '../../outgoing-invoice/enums';
import { EditModel } from '../../outgoing-invoice/models/edit.model';
import {
  DecrementLoadingRequestsCount,
  IncrementLoadingRequestsCount,
  LoadCreditNote,
  LoadCreditNoteList,
  LoadCreditNotePositions,
  UpdateCreditNoteState,
  UpdateCreditNoteUpdatedAt
} from '../store/actions/credit-note.actions';
import { CreditNotePositionModel, CRNPositionsModel } from '../models/credit-note-position.model';
import { UpdatePositionsModel } from '../../outgoing-invoice/models';
import { environment } from 'projects/workspace/src/environments/environment';
import { ToasterService } from 'common/src/modules/ui-components/toaster';
import { CommonModalsActionsEnum, InfoModalComponent, WarningModalComponent } from 'common/src/modules/modals/modals-common';
import { getAnotherUserEditErrorModalData } from 'common/src/modules/modals/modals-common/common-modal.config';
import { BankTransferMethodsEnum, UIStatesEnum } from 'common/src/models';
import { FileUploadParams } from 'common/src/models/file-upload-params.model';
import { DocumentTypesUppercaseEnum } from 'common/src/modules/modals/modals-common/link-document-modal/enums/ducument-types.enum';
import { AvailableSourcesModel } from '../models/available-sources.model';
import { EraCostMismatchModalData, EraQuantityMismatchModalData } from '../pages/credit-note/credit-note.config';
import { PurchaseOrder } from '../../purchase-order/models';
import { DEFAULT_SORT_DIRECTION } from '../../shared/constants';
import { selectCompanyProfile } from '../../administration/store/selectors';
import { CompanyProfile } from '../../administration/models/company-profile.model';

@Injectable({
  providedIn: 'root'
})
export class CreditNoteApiService {
  public wid: number;
  private readonly apiEndpoint: string = `${environment.javaApiVersion}/crn`;

  private apiUrl(url: string = ''): string {
    return this.apiEndpoint + url;
  }

  constructor(
    private readonly toasterService: ToasterService,
    private readonly translateService: TranslateService,
    private readonly http: HttpClient,
    private readonly dialog: MatDialog,
    private readonly store: Store<AppState>
  ) {
    this.store.select(selectCompanyProfile)
      .subscribe((profile: CompanyProfile) => {
        this.wid = profile.workspaceId;
      });
  }

  // public createCreditNoteBlank(): Observable<CreditNoteModel> {
  //   return this.http.post<ResponseModel<CreditNoteModel>>(this.apiUrl('/blank'), {})
  //     .pipe(
  //       map((data: ResponseModel<CreditNoteModel>) => data.data),
  //       catchError(error => {
  //         this.handlePopupErrors(error);
  //         return throwError(error);
  //       })
  //     );
  // }

  public createCreditNoteForInvoice(invoiceId: number, purpose: CreditNotePurposeEnum, documentDirection?: PaymentDirectionEnum): Observable<CreditNoteModel> {
    const body = { invoiceId, purpose, documentDirection };
    return this.http.post<ResponseModel<CreditNoteModel>>(this.apiUrl('/create-predefined'), body)
      .pipe(
        map((data: ResponseModel<CreditNoteModel>) => data.data),
        catchError(error => {
          this.handlePopupErrors(error, invoiceId);
          return throwError(error);
        })
      );
  }

  public getCreditNoteList(
    page: string = '1',
    per_page: string = '100',
    sort: FilterModelNew = { sortBy: 'tracking.updatedAt', direction: DEFAULT_SORT_DIRECTION },
    status: CreditNotesListTabsEnum,
    filters: any = {}
  ): Observable<ResponseList<CreditNoteModel>> {
    const params = {
      page,
      sortBy: sort.sortBy,
      direction: sort.direction,
      length: per_page
    };

    if (status === CreditNotesListTabsEnum.ALL) {
      params['statuses'] = [
        CreditNotesListTabsEnum.DRAFT,
        CreditNotesListTabsEnum.OPEN,
        CreditNotesListTabsEnum.PAID,
        CreditNotesListTabsEnum.CANCELED,
        CreditNotesListTabsEnum.DELETED,
      ].join(',');
      params['allStatus'] = 'true';
    } else {
      params['status'] = status;
    }

    if (filters.hasOwnProperty('payment') && filters.payment.includes(BankTransferMethodsEnum.SEPA_DIRECT_DEBIT)) {
      filters.payment = filters.payment.filter(itm => itm !== BankTransferMethodsEnum.SEPA_DIRECT_DEBIT);
      filters['bankTransferMethod'] = [BankTransferMethodsEnum.SEPA_DIRECT_DEBIT];
    }

    for (const [key, value] of Object.entries(filters)) {
      params[key] = Array.isArray(value) ? value.join(',') : value.toString();
    }

    return this.http.get<ResponseList<CreditNoteModel>>(this.apiEndpoint, { params })
      .pipe(tap((data: ResponseList<CreditNoteModel>) => {
        this.store.dispatch(LoadCreditNoteList({
            creditNoteListData: {
              [data.pagination.page]: {
                pagination: data.pagination,
                sort,
                search: filters.search,
                data: data.data
              }
            },
            status,
            page: data.pagination.page
          }));
      }));
  }

  getCreditNotesFilterRunpleIds(): Observable<{[key in CreditNotesListTabsEnum]?: {key: number; value: string}[]}> {
    const params = {
      wid: this.wid.toString()
    };
    return this.http.get<ResponseModel<{[key in CreditNotesListTabsEnum]?: {key: number; value: string}[]}>>(`${this.apiEndpoint}/filter/rids`, { params })
      .pipe(map((response: ResponseModel<{[key in CreditNotesListTabsEnum]?: {key: number; value: string}[]}>) => response.data));
  }

  getCreditNotesFilterCostCenters(status: CreditNotesListTabsEnum): Observable<{ key: number; value: string }[]> {
    const params = {
      wid: this.wid.toString()
    };

    if (status === CreditNotesListTabsEnum.ALL) {
      params['status'] = [
        CreditNotesListTabsEnum.DRAFT,
        CreditNotesListTabsEnum.OPEN,
        CreditNotesListTabsEnum.PAID,
        CreditNotesListTabsEnum.CANCELED,
        CreditNotesListTabsEnum.DELETED,
      ].join(',');
    } else {
      params['status'] = status;
    }

    return this.http.get<ResponseModel<{ key: number; value: string }[]>>(`${this.apiEndpoint}/filter/cost-center`, { params })
      .pipe(map((response: ResponseModel<{ key: number; value: string }[]>) => response.data));
  }

  getCreditNotesFilterBranchOffice(status: CreditNotesListTabsEnum): Observable<{ key: number; value: string }[]> {
    const params = {
      wid: this.wid.toString()
    };

    if (status === CreditNotesListTabsEnum.ALL) {
      params['status'] = [
        CreditNotesListTabsEnum.DRAFT,
        CreditNotesListTabsEnum.OPEN,
        CreditNotesListTabsEnum.PAID,
        CreditNotesListTabsEnum.CANCELED,
        CreditNotesListTabsEnum.DELETED,
      ].join(',');
    } else {
      params['status'] = status;
    }

    return this.http.get<ResponseModel<{ key: number; value: string }[]>>(`${this.apiEndpoint}/filter/branch-office`, { params })
      .pipe(map((response: ResponseModel<{ key: number; value: string }[]>) => response.data));
  }

  getCreditNotesFilterGeneralProducts(status: CreditNotesListTabsEnum): Observable<any[]> {
    const params = {};

    if (status === CreditNotesListTabsEnum.ALL) {
      params['status'] = [
        CreditNotesListTabsEnum.DRAFT,
        CreditNotesListTabsEnum.OPEN,
        CreditNotesListTabsEnum.PAID,
        CreditNotesListTabsEnum.CANCELED,
        CreditNotesListTabsEnum.DELETED,
      ].join(',');
    } else {
      params['status'] = status;
    }

    return this.http.get<ResponseModel<any[]>>(`${this.apiEndpoint}/filter/general-products`, { params })
      .pipe(map((response: ResponseModel<any[]>) => response.data));
  }

  getCreditNotesFilterLinkedDocuments(): Observable<any[]> {
    return this.http.get<ResponseModel<any[]>>(`${this.apiEndpoint}/filter/linkedDocuments`)
      .pipe(map((response: ResponseModel<any[]>) => response.data));
  }

  // getCreditNotesFilterIBANs(): Observable<string[]> {
  //   return this.http.get<ResponseModel<string[]>>(`${this.apiEndpoint}/filter/ibans`)
  //     .pipe(
  //       map((response: ResponseModel<string[]>) => {
  //         return response.data
  //           .filter(itm => !!itm)
  //           .sort((a, b) => a.localeCompare(b));
  //       })
  //     );
  // }

  getCreditNotesFilterExternalNumbers(): Observable<string[]> {
    return this.http.get<ResponseModel<string[]>>(`${this.apiEndpoint}/filter/extnumbers`)
      .pipe(
        map((response: ResponseModel<string[]>) => {
          return response.data
            .filter(itm => !!itm)
            .sort((a, b) => a.localeCompare(b));
        })
      );
  }

  public getCreditNotesListTotals(status: CreditNotesListTabsEnum,  filters: any = {}): Observable<CreditNotesListTotalsModel> {
    const params = {};
    if (filters.hasOwnProperty('payment') && filters.payment.includes(BankTransferMethodsEnum.SEPA_DIRECT_DEBIT)) {
      filters.payment = filters.payment.filter(itm => itm !== BankTransferMethodsEnum.SEPA_DIRECT_DEBIT);
      filters['bankTransferMethod'] = [BankTransferMethodsEnum.SEPA_DIRECT_DEBIT];
    }
    for (const [key, value] of Object.entries(filters)) {
      params[key] = Array.isArray(value) ? value.join(',') : value.toString();
    }
    return this.http.get<ResponseModel<CreditNotesListTotalsModel>>(`${this.apiEndpoint}/totals/${status}`, {params})
      .pipe(map((data: ResponseModel<CreditNotesListTotalsModel>) => data.data));
  }

  public getCreditNoteByIdRaw(id: number): Observable<CreditNoteModel> {
    return this.http.get<ResponseModel<CreditNoteModel>>(this.apiUrl(`/${id}`))
      .pipe(map((response: ResponseModel<CreditNoteModel>) => response.data));
  }

  public getCreditNoteById(id: number): Observable<CreditNoteModel> {
    this.store.dispatch(IncrementLoadingRequestsCount());

    return this.http.get<ResponseModel<CreditNoteModel>>(this.apiUrl(`/${id}`))
      .pipe(
        tap((response: ResponseModel<CreditNoteModel>) => {
          this.store.dispatch(LoadCreditNote({creditNote: response.data}));
          this.store.dispatch(UpdateCreditNoteUpdatedAt({ updatedAt: new Date() }));
        }),
        map((response: ResponseModel<CreditNoteModel>) => response.data),
        finalize(() => this.store.dispatch(DecrementLoadingRequestsCount())),
      );
  }

  public updateCreditNotePart(id: number, fieldData: FormInputChangedModel): Observable<CreditNoteModel> {
    this.store.dispatch(IncrementLoadingRequestsCount());

    return this.http.patch<ResponseModel<CreditNoteModel>>(`${this.apiEndpoint}/${id}`, fieldData)
      .pipe(
        tap((response: ResponseModel<CreditNoteModel>) => {
          this.store.dispatch(LoadCreditNote({creditNote: response.data}));
          this.store.dispatch(UpdateCreditNoteUpdatedAt({ updatedAt: new Date() }));
        }),
        map((response: ResponseModel<CreditNoteModel>) => response.data),
        finalize(() => this.store.dispatch(DecrementLoadingRequestsCount())),
        catchError(error => {
          this.handlePopupErrors(error, id);
          return throwError(error);
        })
      );
  }

  public bookPaymentBaseOnCreditNote(creditNoteId: number): Observable<PaymentModel> {
    const params = {
      type: 'CRN',
      id: String(creditNoteId)
    };
    return this.http.request<ResponseModel<PaymentModel>>('post', `${environment.javaApiVersion}/payments/based-on`, {params})
      .pipe(
        map((response: ResponseModel<PaymentModel>) => response.data),
        catchError(error => {
          this.handlePopupErrors(error, creditNoteId);
          return throwError(error);
        })
      );
  }

  public allocatePayment(creditNoteId: number): Observable<PaymentModel> {
    return this.http.request<ResponseModel<PaymentModel>>('post', `${this.apiEndpoint}/${creditNoteId}/allocate-payment`)
      .pipe(
        map((response: ResponseModel<PaymentModel>) => response.data),
        catchError(error => {
          this.handlePopupErrors(error, creditNoteId);
          return throwError(error);
        })
      );
  }

  public createCreditNoteFromERA(eraId: number): Observable<CreditNoteModel[]> {
    return this.http.request<ResponseModel<CreditNoteModel[]>>('put', this.apiUrl(`/based-on/${eraId}`))
      .pipe(
        map((data: ResponseModel<CreditNoteModel[]>) => data.data),
        catchError(error => {
          this.handlePopupErrors(error);
          return throwError(error);
        })
      );
  }

  public changeCreditNoteStatus(creditNoteId: number, operation: ChangeStatusOperationsEnum, proceed = false): Observable<CreditNoteModel> {
    const params = new HttpParams()
      .set('proceed', proceed.toString());

    this.store.dispatch(IncrementLoadingRequestsCount());

    return this.http.patch<ResponseModel<CreditNoteModel>>(this.apiUrl(`/${creditNoteId}/${operation}`), {}, { params })
      .pipe(
        tap((response: ResponseModel<CreditNoteModel>) => {
          this.store.dispatch(UpdateCreditNoteUpdatedAt({ updatedAt: new Date() }));
          this.store.dispatch(LoadCreditNote({creditNote: response.data as CreditNoteModel}));
        }),
        finalize(() => this.store.dispatch(DecrementLoadingRequestsCount())),
        map((data: ResponseModel<CreditNoteModel>) => data.data),
      );
  }

  public changeBatchCreditNoteStatus(ids: number[], operation: ChangeStatusOperationsEnum, proceed = false): Observable<any> {
    const params = new HttpParams()
      .set('proceed', proceed.toString());

    return this.http.patch(this.apiUrl(`/batch/${operation}`), { ids }, { params });
  }

  public startCreditNoteEditing(crnId: number, force = false, proceed = false): Observable<EditModel> {
    const params = new HttpParams()
      .set('force', force.toString())
      .set('proceed', proceed.toString());

    return this.http.get<EditModel>(`accounting/v1/utils/edit-flag/${DocumentTypesUppercaseEnum.CRN}/${crnId}/check-and-set`, {params})
      .pipe(
        tap(() => {
          this.store.dispatch(UpdateCreditNoteState({ currentState: UIStatesEnum.EDIT }));
        }),
        catchError(error => {
          this.handlePopupErrors(error, crnId);
          return throwError(error);
        })
      );
  }

  public finishCreditNoteEditing(crnId: number): Observable<EditModel> {
    return this.http.get<EditModel>(`accounting/v1/utils/edit-flag/${DocumentTypesUppercaseEnum.CRN}/${crnId}/commit`)
      .pipe(
        tap(() => {
          this.store.dispatch(UpdateCreditNoteState({ currentState: UIStatesEnum.VIEW }));
        }),
        catchError(error => {
          this.handlePopupErrors(error, null);
          return throwError(error);
        })
      );
  }

  public getCreditNotePositions(crnId: number): Observable<CRNPositionsModel> {
    return this.http.get<ResponseModel<CRNPositionsModel>>(`${this.apiEndpoint}/${crnId}/positions`)
      .pipe(
        map((data: ResponseModel<CRNPositionsModel>) => data.data),
        tap((positions: CRNPositionsModel) => {
          this.store.dispatch(LoadCreditNotePositions({positions: positions}))
        }),
      );
  }

  public getAvailableCreditNotePositions(crnId: number): Observable<CRNPositionsModel> {
    return this.http.get<ResponseModel<CRNPositionsModel>>(`${this.apiEndpoint}/${crnId}/available-positions`)
      .pipe(
        map((data: ResponseModel<CRNPositionsModel>) => data.data)
      );
  }

  // public addCreditNotePositions(
  //   crnId: number,
  //   positionIds: number[] = [],
  //   arbitraryPositions: Partial<CreditNotePositionModel>[] = []
  // ): Observable<CRNPositionsModel> {
  //   this.store.dispatch(IncrementLoadingRequestsCount());
  //   const body = {
  //     ids: positionIds,
  //     positions: arbitraryPositions
  //   };
  //
  //   return this.http.post<ResponseModel<CRNPositionsModel>>(`${this.apiEndpoint}/${crnId}/positions`, body)
  //     .pipe(
  //       tap(() => {
  //         this.store.dispatch(UpdateCreditNoteUpdatedAt({ updatedAt: new Date() }));
  //       }),
  //       map((data: ResponseModel<CRNPositionsModel>) => data.data),
  //       finalize(() => this.store.dispatch(DecrementLoadingRequestsCount())),
  //       catchError(error => {
  //         this.handlePopupErrors(error, crnId);
  //         return throwError(error);
  //       })
  //     );
  // }

  // public addCreditNoteCommonPosition(crnId: number, position: Partial<CreditNotePositionModel>): Observable<CRNPositionsModel> {
  //   this.store.dispatch(IncrementLoadingRequestsCount());
  //
  //   return this.http.post<ResponseModel<CRNPositionsModel>>(`${this.apiEndpoint}/${crnId}/positions/create`, position)
  //     .pipe(
  //       tap(() => {
  //         this.store.dispatch(UpdateCreditNoteUpdatedAt({ updatedAt: new Date() }));
  //       }),
  //       map((data: ResponseModel<CRNPositionsModel>) => data.data),
  //       finalize(() => this.store.dispatch(DecrementLoadingRequestsCount())),
  //       catchError(error => {
  //         this.handlePopupErrors(error, crnId);
  //         return throwError(error);
  //       })
  //     );
  // }

  public createCorrectionPositions(
    crnId: number,
    body: { targetAmountNet: number; targetVat: number; targetAmountGross: number; }
  ): Observable<CRNPositionsModel> {
    this.store.dispatch(IncrementLoadingRequestsCount());

    return this.http.post<ResponseModel<CRNPositionsModel>>(`${this.apiEndpoint}/${crnId}/positions/correction`, body)
      .pipe(
        tap(() => {
          this.store.dispatch(UpdateCreditNoteUpdatedAt({ updatedAt: new Date() }));
        }),
        map((data: ResponseModel<CRNPositionsModel>) => data.data),
        finalize(() => this.store.dispatch(DecrementLoadingRequestsCount())),
        catchError(error => {
          this.handlePopupErrors(error, crnId);
          return throwError(error);
        })
      );
  }

  public createPositionByPredefinedForm(crnId: number, position: Partial<CreditNotePositionModel>): Observable<CRNPositionsModel> {
    this.store.dispatch(IncrementLoadingRequestsCount());
    return this.http.post<ResponseModel<CRNPositionsModel>>(`${this.apiEndpoint}/${crnId}/positions-predefined`, position)
      .pipe(
        tap(() => {
          this.store.dispatch(UpdateCreditNoteUpdatedAt({ updatedAt: new Date() }));
        }),
        map((data: ResponseModel<CRNPositionsModel>) => data.data),
        tap((positions: CRNPositionsModel) => {
          this.store.dispatch(UpdateCreditNoteUpdatedAt({ updatedAt: new Date() }));
          this.store.dispatch(LoadCreditNotePositions({positions: positions}));
        }),
        finalize(() => this.store.dispatch(DecrementLoadingRequestsCount())),
        catchError(error => {
          this.handlePopupErrors(error, crnId);
          return throwError(error);
        })
      );
  }

  public updateCreditNotePosition(
    creditNoteId: number,
    updatePosition: UpdatePositionsModel
  ): Observable<CreditNotePositionModel> {
    this.store.dispatch(IncrementLoadingRequestsCount());

    return this.http.patch<ResponseModel<CreditNotePositionModel>>(
      `${this.apiEndpoint}/${creditNoteId}/positions/${updatePosition.positionId}`,
      updatePosition
    ).pipe(
      tap(() => {
        this.store.dispatch(UpdateCreditNoteUpdatedAt({ updatedAt: new Date() }));
      }),
      map((data: ResponseModel<CreditNotePositionModel>) => data.data),
      finalize(() => this.store.dispatch(DecrementLoadingRequestsCount())),
      catchError(error => {
        this.handlePopupErrors(error, creditNoteId);
        return throwError(error);
      })
    );
  }

  public updateCreditNoteBundledProduct(
    creditNoteId: number,
    positionId: number,
    bundleProductId: number,
    fieldName: string,
    fieldValue: any
  ): Observable<CreditNotePositionModel> {
    const body = { fieldName, fieldValue };

    return this.http.post<ResponseModel<CreditNotePositionModel>>(
      this.apiUrl(`/${creditNoteId}/positions/${positionId}/bundle-position/${bundleProductId}`),
      body
    )
      .pipe(
        map((data: ResponseModel<CreditNotePositionModel>) => data.data),
        catchError(error => {
          this.handlePopupErrors(error, creditNoteId);
          return throwError(error);
        })
      );
  }

  public deleteCreditNotePositions(creditNoteId: number, ids: number[]): Observable<CRNPositionsModel> {
    this.store.dispatch(IncrementLoadingRequestsCount());
    const body = { ids };

    return this.http.request<ResponseModel<CRNPositionsModel>>(
      'delete',
      `${this.apiEndpoint}/${creditNoteId}/positions`,
      {body}
    ).pipe(
      tap(() => {
        this.store.dispatch(UpdateCreditNoteUpdatedAt({ updatedAt: new Date() }));
      }),
      map((data: ResponseModel<CRNPositionsModel>) => data.data),
      finalize(() => this.store.dispatch(DecrementLoadingRequestsCount())),
      catchError(error => {
        this.handlePopupErrors(error, creditNoteId);
        return throwError(error);
      })
    );
  }

  changePositionOrder(posId: number, moveToOrder: number, creditNoteId: number): any {
    const body = {
      orderPosition: moveToOrder,
      id: posId,
    }
    return this.http.patch(`${this.apiEndpoint}/${creditNoteId}/positions/${posId}/order/${moveToOrder}`, body)
      .pipe(
        map((data: ResponseModel<PurchaseOrder>) => data.data),
        catchError(error => {
          this.showMsg('error', error.error.message);
          return throwError(error);
        })
      );
  }

  public getPositionsCount(creditNoteId: number): Observable<number> {
    return this.http.get(`${this.apiEndpoint}/${creditNoteId}/positions/count`)
      .pipe(map((data: ResponseModel<number>) => data.data));
  }

  public getCreditNotesCounters(): Observable<CreditNotesCountersModel> {
    return this.http.get<ResponseModel<CreditNotesCountersModel>>(`${this.apiEndpoint}/count/by-status`)
      .pipe(map((data: ResponseModel<CreditNotesCountersModel>) => data.data));
  }

  public getCreditNoteAvailableSources(crnId: number): Observable<AvailableSourcesModel[]> {
    return this.http.get<ResponseModel<AvailableSourcesModel[]>>(`${this.apiEndpoint}/${crnId}/available-sources`)
      .pipe(map((data: ResponseModel<AvailableSourcesModel[]>) => data.data));
  }

  public getCreditNoteModalAvailableSources(purpose: CreditNotePurposeEnum): Observable<AvailableSourcesModel[]> {
    const params = new HttpParams().set('purpose', purpose);
    return this.http.get<ResponseModel<AvailableSourcesModel[]>>(`${this.apiEndpoint}/modal/available-sources`, {params})
      .pipe(map((data: ResponseModel<AvailableSourcesModel[]>) => data.data));
  }

  public reCheckVatStatus(crnId: number): Observable<CreditNoteModel> {
    return this.http.request<ResponseModel<CreditNoteModel>>('post', `${this.apiEndpoint}/${crnId}/vat-status`)
      .pipe(
        tap((response: ResponseModel<CreditNoteModel>) => {
          this.store.dispatch(LoadCreditNote({creditNote: response.data as CreditNoteModel}));
          this.store.dispatch(UpdateCreditNoteUpdatedAt({ updatedAt: new Date() }));
        }),
        map((data: ResponseModel<CreditNoteModel>) => data.data)
      );
  }

  public getCreditNoteListExport(status: string): Observable<FileUploadParams> {
    const fileParams: FileUploadParams = {
      url: `${this.apiEndpoint}/download?status=${status}`,
      type: 'zip',
    };
    return of(fileParams);
  }

  public getCreditNoteListExportAll(): Observable<FileUploadParams> {
    const fileParams: FileUploadParams = {
      url: `${this.apiEndpoint}/download/all`,
      type: 'zip',
    };
    return of(fileParams);
  }

  public downloadCreditNoteImgFile(crnId: number, imageId: number, title: string): Observable<Partial<FileUploadParams>> {
    const fileParams: Partial<FileUploadParams> = {
      url: `${this.apiEndpoint}/${crnId}/files/${imageId}`,
      title,
    };
    return of(fileParams);
  }

  public deleteFile(crnId: number | string, fileId: string): Observable<CreditNoteModel> {
    this.store.dispatch(IncrementLoadingRequestsCount());

    return this.http.delete<ResponseModel<CreditNoteModel>>(`${this.apiEndpoint}/${crnId}/files/${fileId}`)
      .pipe(
        tap((response: ResponseModel<CreditNoteModel>) => {
          this.store.dispatch(LoadCreditNote({creditNote: response.data as CreditNoteModel}));
          this.store.dispatch(UpdateCreditNoteUpdatedAt({ updatedAt: new Date() }));
        }),
        map((response: ResponseModel<CreditNoteModel>) => response.data),
        finalize(() => this.store.dispatch(DecrementLoadingRequestsCount())),
        catchError(error => {
          this.handlePopupErrors(error, crnId);
          return throwError(error);
        })
      );
  }

  // public sendCreditNoteEmail(crnId: number): Observable<any> {
  //   return this.http.post(this.apiUrl(`/${crnId}/email`), {})
  //     .pipe(
  //       catchError(error => {
  //         this.handlePopupErrors(error, crnId);
  //         return throwError(error);
  //       })
  //     );
  // }

  public getUploadPdfLink(id: string | number): string {
    return `${environment.javaApiUrl}${this.apiEndpoint}/${id}/files/upload`;
  }

  public getCRNExportParams(id: number, title: string): Observable<FileUploadParams> {
    const fileParams: FileUploadParams = {
      url: `${environment.javaApiVersion}/pdf/crn?id=${id}`,
      type: 'pdf',
      title,
    };
    return of(fileParams);
  }

  public downloadCRNXMLFile(ids: number[]): Observable<Partial<FileUploadParams>> {
    const fileParams: Partial<FileUploadParams> = {
      url: this.apiUrl(`/xml?ids=${ids.join(',')}`),
      type: 'xml',
    };
    return of(fileParams);
  }

  public exportCreditNotesPdfBatchUrl(): string {
    return `${environment.javaApiVersion}/pdf/generated/batch?wid=${this.wid}&documentType=${DocumentTypesUppercaseEnum.CRN}`;
  }

  public linkPayment(crnId: number, paymentId: number): Observable<any[]> {
    return this.http.get<ResponseModel<any[]>>(`${this.apiEndpoint}/${crnId}/payments/${paymentId}/link`)
      .pipe(
        map((data: ResponseModel<any[]>) => data.data),
        catchError(error => {
          this.handlePopupErrors(error);
          return throwError(error);
        })
      );
  }

  public getPaymentsAvailable(crnId: number): Observable<PaymentModel[]> {
    return this.http.get<ResponseModel<PaymentModel[]>>(`${this.apiEndpoint}/${crnId}/payments/available`)
      .pipe(
        map((data: ResponseModel<PaymentModel[]>) => data.data)
      );
  }

  public getPaymentRecommendations(crnId: number): Observable<PaymentRecommendationModel[]> {
    return this.http.get<ResponseModel<PaymentRecommendationModel[]>>(`${this.apiEndpoint}/${crnId}/payment-recommendations`)
      .pipe(
        map((data: ResponseModel<PaymentRecommendationModel[]>) => data.data)
      );
  }

  @DisplayToaster({ showErrorMessage: true })
  public dismissPaymentRecommendation(crnId: number, paymentId: number): Observable<PaymentRecommendationModel[]> {
    return this.http.post<ResponseModel<PaymentRecommendationModel[]>>(
      `${this.apiEndpoint}/${crnId}/payment-recommendations/${paymentId}/dismiss`, {}
    )
      .pipe(
        map((data: ResponseModel<PaymentRecommendationModel[]>) => data.data)
      );
  }

  @DisplayToaster({ showErrorMessage: true, showSuccessMessage: true, successMsg: 'PAYMENT.HAS_BEEN_BOOKED' })
  public bookPaymentRecommendation(crnId: number, paymentId: number): Observable<PaymentModel> {
    return this.http.post<ResponseModel<PaymentModel>>(
      `${this.apiEndpoint}/${crnId}/payment-recommendations/${paymentId}/book`, {}
    )
      .pipe(
        map((data: ResponseModel<PaymentModel>) => data.data)
      );
  }

  public showMsg(type: string, message: string): void {
    this.toasterService.notify({ type, message });
  }

  private handlePopupErrors(error: HttpErrorResponse, crnId?: string | number): void {
    error.error.errors.forEach(errorText => {
      switch (errorText) {
        case 'anotherUserEditError':
        {
          const dialog = this.dialog.open(WarningModalComponent, {
            data: getAnotherUserEditErrorModalData(
              {
                document: error.error.data.entityName,
                userName: error.error.data.userName,
              },
              this.translateService
            ),
            backdropClass: 'backdrop-none',
          });

          dialog.afterClosed().subscribe(res => {
            if (res === CommonModalsActionsEnum.CONFIRM) {
              this.startCreditNoteEditing(+crnId, true).subscribe();
              this.getCreditNoteById(+crnId).subscribe();
            }
          });
        }
          break;
        case 'The document has been sent to the accountant, if you edit it, you will need to re-send the document to the accountant. Proceed?':
        {
          const dialog = this.dialog.open(WarningModalComponent, {
            data: {
              title: 'COMMON.DOC_SENT_TO_ACCOUNTANT',
              message: 'COMMON.DOC_SENT_TO_ACCOUNTANT_MSG',
              confirmBtnText: 'BUTTON.EDIT',
              confirmBtnIcon: 'edit'
            }
          });

          dialog.afterClosed().subscribe(res => {
            if (res === CommonModalsActionsEnum.CONFIRM) {
              this.startCreditNoteEditing(+crnId, false, true).subscribe();
              this.getCreditNoteById(+crnId).subscribe();
            }
          });
        }
          break;
        case 'notEditModeError':
          const documentName = this.translateService.instant('APP.CREDIT_NOTE');
          this.showMsg('warning', this.translateService.instant('COMMON.DOC_UPDATED_BY_USER', { document: documentName }));
          this.store.dispatch(UpdateCreditNoteState({ currentState: UIStatesEnum.VIEW }));
          this.getCreditNoteById(+crnId).subscribe();
          break;
        case 'Mismatched quantity.':
        case 'Die Menge stimmt nich überein.':
          this.dialog.open(InfoModalComponent, {
            data: EraQuantityMismatchModalData
          });
          break;
        case 'Mismatched delivery costs.':
        case 'Lieferkosten stimmen nich überein.':
          this.dialog.open(InfoModalComponent, {
            data: EraCostMismatchModalData
          });
          break;
        default:
          this.showMsg('error', errorText);
          break
      }
    });
  }

}
